import Copyright from "@app-components/copyright/copyright.vue";
import FooterMenu from "@app-components/footer-menu/footer-menu.vue";
import Layer from "@app-components/layer.vue";
import Logo from "@app-components/logo/logo.vue";
import Menubar from "@app-components/menubar/menubar.vue";
import MissingFunctionalities from "@app-components/missing-functionalities/missing-functionalities.vue";
import QSettings from "@app-components/q-settings/q-settings.vue";
import ServiceMenu from "@app-components/service-menu/service-menu.vue";
import { isDevSettingsEnabled as checkIsDevSettingsEnabled, useSettings } from "@app-composables/settings";
import useDebugDialog from "@app-composables/use-debug-dialog";
import { isB2CService, isFullscreenPage, isSkippable } from "@app-router/helpers";
import { attractionsBook, attractionsOfferDetail, landing } from "@app-router/routes/attractions";
import { home } from "@app-router/routes/reservations";
import useAppStore from "@app-store/app";
import store from "@app-store/index";
import { setupPersistentModules } from "@app-store/persistent.plugin";
import useSystemStore from "@app-store/system";
import { isErrorPage } from "@app-utilities/const";
import EventsManager from "@app-utilities/events-manager";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { azurePopupWindowFocus } from "@app-utilities/popup";
import { initToastUtil } from "@app-utilities/toasts";
import { QOrchestrator, QOverlay, useResponsiveUtilities, useToast } from "@qamf/lighthouse";
import { useObservability, useObservabilityManager } from "@qamf/observability";
import { debounce } from "lodash-es";
import {
	computed,
	defineComponent,
	nextTick,
	onMounted,
	onUnmounted,
	ref,
	watch
} from "vue";
import { useRoute, useRouter } from "vue-router";

export default defineComponent({
	name: "Bowler",
	components: {
		QOverlay,
		QOrchestrator,
		FooterMenu,
		Copyright,
		Layer,
		Logo,
		Menubar,
		ServiceMenu,
		MissingFunctionalities,
		QSettings
	},
	props: {},
	setup() {
		const { environment } = useSettings();
		const appStore = useAppStore();
		const systemStore = useSystemStore();
		const { setupClarity } = useObservabilityManager();
		const logo = ref<typeof Logo>();
		const serviceMenu = ref<typeof ServiceMenu>();
		const { debugDialogIsOpen: showSettings } = useDebugDialog();
		const isDevSettingsEnabled = computed(() => checkIsDevSettingsEnabled());
		const router = useRouter();
		const route = useRoute();
		const toastController = useToast();
		const responsiveUtilities = useResponsiveUtilities();
		const isProduction = computed(() => environment.value === "production");
		const isSmall = computed(() => responsiveUtilities.isSmall.value );
		const isB2CServiceView = computed(() => {
			return isB2CService(route);
		});
		const isFullscreenView = computed(() => {
			return isFullscreenPage(route);
		});
		const isSkippableView = computed(() => {
			return isSkippable(route);
		});
		const isErrorView = computed(() => {
			return isErrorPage(route.name);
		});
		const isPopupOpen = computed(() => {
			return appStore.azurePopupWindowIsOpen;
		});
		const isMaintenanceModeActive = computed(() => {
			return systemStore.maintenanceMode;
		});
		const loadingView = computed(() => {
			return appStore.loadingView;
		});
		const isMissingRequiredFunctionalities = computed(() => {
			return systemStore.functionalities && !systemStore.functionalities?.WebReservations && !route.meta.isStandalonePage;
		});
		const hasWebReservation = computed(() => {
			return Boolean(systemStore.functionalities?.WebReservations?.Enabled);
		});
		const currentViewName = computed(() => {
			return route.name;
		});
		const isBookingHome = computed(() => {
			return route.name === home || route.name === landing || route.name === attractionsBook;
		});
		const footerContents = computed(() => {
			return systemStore.templateCommonParts?.Footers ?? [];
		});
		const isDisabled = computed(() => {
			return route.name === "qamf-maintenance-mode";
		});
		const isHeaderHidden = computed(() => {
			return (currentViewName.value === attractionsOfferDetail && isSmall.value) || route.meta.isStandalonePage;
		});
		const headerLayer = ref<any>();
		const currentView = ref<any>();
		const currentViewComponent = ref<any>();
		const isSticky = ref(false);
		const onResizeApp = () => {
			EventsManager.emitWindowResize();
			return nextTick();
		};
		const mainOverlayClicked = () => {
			azurePopupWindowFocus();
		};
		const formatClass = (index: number) => {
			const position = ["left", "right"];
			return position[index];
		};
		const onScrollApp = () => {
			if (!headerLayer.value) return;
			const stickyHeader = headerLayer.value.$el.scrollTop;
			isSticky.value = window.scrollY > stickyHeader;
		};
		const updateThemeColor = () => {
			const color = window.getComputedStyle(headerLayer.value?.$el ?? document.createElement("div"), null).backgroundColor;
			if (color !== "") localStorage.setItem("themeColorPwa", color);
		};
		watch(
			() => loadingView.value,
			async(old: boolean, loading: boolean) => {
				await nextTick();
				if (!loading) return;
				const layer = headerLayer.value?.$el;
				if (!layer) return;
				layer.scrollIntoView({
					behavior: "smooth",
					block: "start"
				});
			}
		);
		const getCurrentViewProxyInfo = async() => {
			await nextTick();
			if (currentView.value) {
				const currentComponent = currentView.value.$.subTree.component;
				currentViewComponent.value = currentComponent.proxy;
			}
		};
		watch(route, async() => {
			await getCurrentViewProxyInfo();
		});
		onMounted(async() => {
			responsiveUtilities.init();
			window.onresize = debounce(onResizeApp, 200);
			onResizeApp();
			window.onscroll = debounce(() => EventsManager.emitScrollWindow());
			EventsManager.onScrollWindow(onScrollApp);
			onScrollApp();
			const { trackEvent } = useObservability();
			await appStore.ensureRequiredBrowserFeatures();
			if (!appStore.hasAllRequiredBrowserFeatures()) {
				trackEvent(AppInsightsEvent.RedirectToDisabledFeatures, {
					browser: appStore.browser,
					isValidBrowser: appStore.isValidBrowser(),
					browserFeatures: appStore.requiredBrowserFeatures
				});
				return router
					.replace({
						name: "disabled-features"
					})
					.catch(() => { });
			}
			if (!appStore.isValidBrowser()) {
				trackEvent(AppInsightsEvent.RedirectToUnsupportedBrowser, {
					browser: appStore.browser,
					isValidBrowser: appStore.isValidBrowser(),
					browserFeatures: appStore.requiredBrowserFeatures
				});
				return router
					.replace({
						name: "browser-not-supported"
					})
					.catch(() => { });
			}
			const usePersistence = setupPersistentModules({
				name: "BowlerVuexModules",
				stores: [
					{ name: "user", params: { autoIncrement: false } },
					{ name: "cart", params: { autoIncrement: false } },
					{ name: "reservation", params: { autoIncrement: false } }
				]
			});
			if (isProduction.value) setupClarity("nbdhiukmzz");
			usePersistence(store);
			initToastUtil(toastController);
		});
		onUnmounted(() => {
			responsiveUtilities.destroy();
		});
		return {
			isFullscreenView,
			isB2CServiceView,
			isSkippableView,
			isErrorView,
			isMaintenanceModeActive,
			isMissingRequiredFunctionalities,
			hasWebReservation,
			isPopupOpen,
			loadingView,
			currentViewName,
			isBookingHome,
			footerContents,
			isDisabled,
			headerLayer,
			currentView,
			isSticky,
			serviceMenu,
			logo,
			mainOverlayClicked,
			formatClass,
			updateThemeColor,
			isHeaderHidden,
			showSettings,
			isDevSettingsEnabled
		};
	}
});
